import Bugsnag from "@bugsnag/js"
import { combineEpics, Epic } from "redux-observable"
import { merge, of } from "rxjs"
import { catchError } from "rxjs/operators"
import { logToBugsnag } from "../../bugsnag/bugsnag"
import { logger } from "../../utils/loggerWrap"
import { logOutSuccess } from "../layouts/header/actions"
import type { StateObject } from "../redux/types"
import type { ApiServices } from "../../api/createServices"
import type { ConfigType } from "../../utils/config"

export type EpicDependencies = {
    api: ApiServices
    idgen: IdGenerator
    config: ConfigType
    getState: () => StateObject
}

export type IdGenerator = {
    next: () => string
}

const MAX_DESCRIPTION_LENGTH = 200

const SESSION_EXPIRED_MESSAGE = "You've Been Logged Out Due To Session Expiration"

const describe = (thrown: unknown): string => {
    if (typeof thrown === "object" && thrown !== null) {
        const { type, status } = thrown as Record<string, any>
        const name = typeof type === "string" && type !== "" ? type : "non-error"
        return status == null ? name : `${name} (status ${status})`
    }

    return `non-error: ${String(thrown).slice(0, MAX_DESCRIPTION_LENGTH)}`
}

const isSessionExpired = (thrown: unknown): boolean => {
    if (typeof thrown !== "object" || thrown === null) {
        return false
    }

    const { status } = thrown as Record<string, any>
    return status === 401 || status === 403
}

export const guardEpic =
    (epic: Epic): Epic =>
    (action$, state$, deps) =>
        epic(action$, state$, deps).pipe(
            catchError((thrown, restart) => {
                if (isSessionExpired(thrown)) {
                    logger.warn("Session expired in epic", { thrown: thrown })

                    return merge(of(logOutSuccess(SESSION_EXPIRED_MESSAGE)), restart)
                }

                const error = thrown instanceof Error ? thrown : new Error(`Epic failed: ${describe(thrown)}`)

                logger.error("Unhandled error in epic", { thrown: thrown }, error)

                if (logToBugsnag) {
                    Bugsnag.notify(error, event => {
                        event.addMetadata("epic", { thrown: thrown })
                    })
                }

                return restart
            })
        )

const configureEpics = (epics: any[]) => combineEpics(...(epics.flat() as Epic[]).map(guardEpic))

export default configureEpics
